import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';

// Relative import on purpose: the barrel also exports this file — going
// through it would create a cycle and drag the interactive graph into
// pure-rendering consumers/tests.
import MatchCard from '../cards/MatchCard';

// ─── Layout constants ─────────────────────────────────────────────────────────
//
//  The bracket is now composed of Competition Design-System CompetitorCards
//  (badge above name, club, rank), so a slot is a richer, taller card than
//  the old single text line. The geometry maths are UNCHANGED — only the
//  constants grew to fit the card; connectors still target match.centerY.
//
//  CARD_HEIGHT    – height of one CompetitorCard slot
//  INNER_SPACING  – divider gap between the two cards inside one match
//  MATCH_SPACING  – gap between consecutive matches in round 1
//  MATCH_HEIGHT   – total height of one match block = CARD_HEIGHT * 2 + INNER_SPACING

const CARD_HEIGHT    = 58;
const PLAYER_HEIGHT  = CARD_HEIGHT;              // kept name for the maths below
const INNER_SPACING  = 6;
const MATCH_SPACING  = 26;
const MATCH_HEIGHT   = PLAYER_HEIGHT * 2 + INNER_SPACING;
const COL_W          = 220;
const CONN_W         = 48;
const STUB           = CONN_W / 2;

// ─── Round label map (fallbacks; localized via bracket.rounds.*) ─────────────

const ROUND_LABELS = {
  F:    'Final',
  TP:   '3rd Place',
  SF:   'Semi-Final',
  QF:   'Quarter-Final',
  OEF:  '1/8 Final',
  '0SF':'1/16 Final',
  OTSF: '1/32 Final',
  OSFF: '1/64 Final',
};

/** Localized round label — shared with the round-list editors. */
export const useRoundLabel = () => {
  const [t] = useTranslation();
  return code => t(`bracket.rounds.${code}`, ROUND_LABELS[code] || code);
};

const THIRD_PLACE = 'TP';

/*
 * HOW DEEP A ROUND SITS — the backend's RoundOrder::stageIndex(): the bronze
 * match and the repechage rungs share the final's stage, a round robin is
 * the only stage. Used to find the round an athlete LEFT in.
 */
const PLAY_ORDER = ['OSFF', 'OTSF', '0SF', 'OEF', 'QF', 'SF', 'F'];
export const stageIndex = code => {
  if ('RR' === code) {
    return 0;
  }
  if (THIRD_PLACE === code || /^R[AB]\d+$/.test(code || '')) {
    return PLAY_ORDER.indexOf('F');
  }

  return PLAY_ORDER.indexOf(code);
};

/**
 * A draw is fully pair-based (kumite / fukugo style) when every row carries
 * a pairType. Kata-style draws mix in LIST rounds (pairType null) and keep
 * their own workflow. Shared by BracketView (layout mode) and
 * InteractiveBracketView (conductability gate).
 */
export const isFullyPaired = results =>
  Array.isArray(results) &&
  results.length > 0 &&
  results.every(r => r.pairType === 'A' || r.pairType === 'S');

// ─── buildBracketData ─────────────────────────────────────────────────────────
//
//  Position maths (match-based, not player-based) — UNCHANGED from before the
//  design-system refactor; only the layout constants above grew.
//
//  Round 0 (earliest / largest):
//    match[i].topY    = i × (MATCH_HEIGHT + MATCH_SPACING)
//    match[i].centerY = match[i].topY + MATCH_HEIGHT / 2
//
//  Round r (r ≥ 1) — centred between its two feeders:
//    match[i].centerY = ( prev[2i].centerY + prev[2i+1].centerY ) / 2
//    match[i].topY    = match[i].centerY − MATCH_HEIGHT / 2
//
//  BYE: if one feeder is missing the match inherits the single feeder's
//  centerY, so the full MATCH_HEIGHT is still reserved.

/**
 * `roundOrder` (optional): explicit play order of round codes, first round
 * first. Supplied for structures whose order cannot be inferred from row
 * counts — a repechage ladder is one fight per rung, so every rung has the
 * same two rows and the count heuristic below cannot tell rung 1 from rung 2.
 * The backend already sends the rounds in play order; passing that through is
 * how the ladder gets drawn in the order it is fought.
 *
 * Omitted for the main elimination tree, which keeps the count heuristic it
 * has always used.
 */
export function buildBracketData(results, roundOrder = null) {
  if (!results || results.length === 0) return { rounds: [], thirdPlace: null, fullyPaired: false };

  const fullyPaired = isFullyPaired(results);

  // The third-place fight is normally a DETACHED match hanging off the final,
  // not a column of the tree. But it can also be handed to this renderer ON
  // ITS OWN — the MSAC repechage draws it below the two ladders that feed it,
  // rather than beside a final it has nothing to do with. With no tree to
  // hang off, it simply is the tree.
  const hasTreeRounds = results.some(r => r.round !== THIRD_PLACE);

  const byRound = {};
  results.forEach(r => {
    if (fullyPaired && hasTreeRounds && r.round === THIRD_PLACE) return;
    if (!byRound[r.round]) byRound[r.round] = [];
    byRound[r.round].push(r);
  });

  const toMatches = (code, items) => {
    const byPair = {};
    items.forEach(item => {
      if (!byPair[item.pair]) byPair[item.pair] = { A: null, S: null };
      byPair[item.pair][item.pairType === 'A' ? 'A' : 'S'] = item;
    });

    return Object.keys(byPair)
      .map(Number)
      .sort((a, b) => a - b)
      .map(pairNum => ({
        id:      `${code}-${pairNum}`,
        round:   code,
        pairNum,
        player1: byPair[pairNum].A,   // null → BYE
        player2: byPair[pairNum].S,   // null → BYE
        topY:    0,
        centerY: 0,
      }));
  };

  const allRounds = Object.entries(byRound).map(([code, items]) => ({
    code,
    matches: toMatches(code, items),
    count: items.length,
    // PAIR-judged (flag) round: every row is an AKA/SHIRO fight slot.
    // LIST-judged (scored) rounds — kata's semi-final/final under
    // FLAGS_TO_SEMI_FINAL/FLAGS_TO_FINAL — carry no pairType and are
    // rendered separately by StageTables, never as a bracket column here
    // (they have no winner-click advancement to visualize, only a
    // qualification list StageTables already draws).
    paired: items.every(item => item.pairType === 'A' || item.pairType === 'S'),
  }));

  // Earliest (largest) round first — unless the caller gave the real order.
  if (Array.isArray(roundOrder) && roundOrder.length > 0) {
    allRounds.sort((a, b) => roundOrder.indexOf(a.code) - roundOrder.indexOf(b.code));
  } else {
    allRounds.sort((a, b) => b.count - a.count);
  }

  // Fully flag-judged (FLAGS_ONLY): every round already qualifies. Mixed
  // formats: stop at the last PAIR-judged round — a LIST-judged round is
  // never part of this elimination tree, however small its count.
  const rounds = fullyPaired ? allRounds : allRounds.filter(r => r.paired);

  if (rounds.length === 0) return { rounds: [], thirdPlace: null, fullyPaired };

  // Round 0: fixed grid — one MATCH_HEIGHT block per match, MATCH_SPACING between
  rounds[0].matches.forEach((m, i) => {
    m.topY    = i * (MATCH_HEIGHT + MATCH_SPACING);
    m.centerY = m.topY + MATCH_HEIGHT / 2;
  });

  // Subsequent rounds: centre on the midpoint of the two feeding matches
  for (let r = 1; r < rounds.length; r++) {
    const prev = rounds[r - 1].matches;
    rounds[r].matches.forEach((m, i) => {
      const f1 = prev[i * 2];
      const f2 = prev[i * 2 + 1];
      m.centerY = (f1 && f2)
        ? (f1.centerY + f2.centerY) / 2
        : (f1 ? f1.centerY : 0);
      m.topY = m.centerY - MATCH_HEIGHT / 2;
    });
  }

  // Third-place fight: detached trailing match, just below the final.
  let thirdPlace = null;
  const tpResults = fullyPaired && hasTreeRounds ? results.filter(r => r.round === THIRD_PLACE) : [];
  if (tpResults.length > 0) {
    const tpMatches = toMatches(THIRD_PLACE, tpResults);
    const final = rounds[rounds.length - 1].matches[0];
    thirdPlace = tpMatches[0];
    thirdPlace.topY = (final ? final.topY : 0) + MATCH_HEIGHT + MATCH_SPACING;
    thirdPlace.centerY = thirdPlace.topY + MATCH_HEIGHT / 2;
  }

  return { rounds, thirdPlace, fullyPaired };
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    overflowX: 'auto',
    overflowY: 'visible',
    // Room on the LEFT for the READY / LIVE / NEXT badge, which stands half
    // outside the card — a ladder's first column sits flush against the
    // scroll edge, and the badge was cut in half there (26 Oct 2026).
    padding: theme.spacing(1, 0, 1, 2.5),
  },
  columns: {
    display:       'flex',
    flexDirection: 'row',
    alignItems:    'flex-start',
  },
  /* MIRRORED layout (official MSAC sheet, Додаток 11): two halves facing a
     final in the centre. The right half reuses the SAME column renderer and
     is flipped horizontally, so the tree grows inward from both sides
     without a second set of layout maths to keep in step. */
  mirrored: {
    display:       'flex',
    flexDirection: 'row',
    alignItems:    'flex-start',
  },
  flipped: {
    transform: 'scaleX(-1)',
  },
  /* Everything with text or interaction is flipped back, so only the
     connector lines end up mirrored — which is the whole point. */
  unflip: {
    transform: 'scaleX(-1)',
  },
  centreFinal: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0,
    padding:        theme.spacing(0, 1),
  },
  colWrapper: {
    display:       'flex',
    flexDirection: 'column',
    flexShrink:    0,
  },
  /* The discipline of a fukugo round, under its name. Deliberately quieter
     than the round label: it qualifies the round, it does not replace it. */
  roundStage: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    opacity: 0.75,
  },
  roundLabel: {
    width:         COL_W,
    height:        24,
    textAlign:     'center',
    fontWeight:    600,
    fontSize:      11,
    color:         theme.palette.text.secondary,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  roundCol: {
    position:   'relative',
    width:      COL_W,
    flexShrink: 0,
  },
  matchLabel: {
    position:   'absolute',
    left:       0,
    right:      0,
    fontSize:   10,
    fontWeight: 600,
    color:      theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    padding:    '0 8px',
  },
  // The absolutely-positioned match block (a MatchCard fills it).
  matchAnchor: {
    position: 'absolute',
    left:     0,
    right:    0,
    height:   MATCH_HEIGHT,
  },
  /* THE PROTOCOL BUTTON — where the organiser drew a dot: at the right edge of
     the pair, level with the line between the two athletes. Small and quiet:
     the bracket is read far more often than a protocol is opened. */
  // On the card's LEFT edge, between the two athletes — the mirror of the
  // «i» on the right (26 Oct 2026: «синім… між двома спортсменами»).
  liveBadge: {
    position: 'absolute',
    left: -16,
    zIndex: 2,
    padding: '1px 6px',
    borderRadius: 8,
    background: '#1976d2',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.08em',
    lineHeight: '16px',
    pointerEvents: 'none',
  },
  nextBadge: { background: '#ef6c00' },
  readyBadge: { background: '#2e7d32' },
  protocolButton: {
    position: 'absolute',
    right: -9,
    width: 18,
    height: 18,
    padding: 0,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    background: '#ffd600',
    color: 'rgba(0,0,0,0.75)',
    fontSize: 11,
    fontWeight: 800,
    lineHeight: '18px',
    zIndex: 2,
    '&:hover': { background: '#ffc400' },
  },
  slotClickable: {
    cursor: 'pointer',
    '&:hover': { background: 'rgba(33,150,243,0.12)' },
  },
  slotDraggable: {
    cursor: 'grab',
    '&:hover': { background: 'rgba(255,152,0,0.12)' },
  },
  // Connector SVG channel between two round columns
  connSpace: {
    position:   'relative',
    flexShrink: 0,
    width:      CONN_W,
  },
  connSvg: {
    position:      'absolute',
    top:           0,
    left:          0,
    overflow:      'visible',
    pointerEvents: 'none',
  },
}));

// ─── slot descriptor ───────────────────────────────────────────────────────────
//
//  Turns one result row into a MatchCard slot: the CompetitorCard props
//  (name / club / rank + winner-loser-empty state) plus `slotProps` — the DOM
//  attributes and interaction handlers that stay bracket-specific (the data
//  testids the app/e2e depend on, winner clicks, drag & drop). MatchCard owns
//  the AKA/SHIRO position; the role belongs to the slot, never the athlete.

/**
 * Has this sheet actually decided its fight?
 *
 * Only CONFIRMED has: PENDING and RUNNING have recorded no outcome, and
 * FINISHED has one the chief secretary has not signed. The distinction is the
 * whole difference between protecting a result and trapping a fight.
 *
 * ── AND THE FIGHT HAS TO STILL BE DECIDED ───────────────────────────────
 *
 * A signed sheet protects the fight it signed. It cannot protect a fight that
 * is no longer there. Delete a conducted category's draw and generate it
 * again and the protocol survives, addressed by round and pair — numbers the
 * new bracket reuses — so it went on locking a slot in a bracket it had never
 * seen, where nobody had won anything.
 *
 * That is a DEADLOCK, and a real competition hit it: the mat could not reopen
 * the sheet (CONFIRMED is terminal), the bracket refused the click, and the
 * correction workflow needs a category whose results are confirmed — which
 * this one could never be, because the engine rightly called its final
 * unfinished. «Ніхто не може ввести результати, адміністратор теж не може»
 * (1 Oct 2026).
 *
 * So the lock asks the BRACKET, not only the sheet: a confirmed protocol over
 * a fight with no winner is signing something that no longer exists, and it
 * must not trap the slot. A healthy signed fight is untouched — it has a
 * winner, which is what it means to be signed.
 */
function isDecidedProtocol(protocol, fightDecided = true) {
  return Boolean(protocol) && 'CONFIRMED' === protocol.state && false !== fightDecided;
}

function makeSlotDescriptor(result, ctx, isEntryRound = true) {
  const { classes, infoOf, clickable, draggable, onSlotClick, onSlotSwap, annotation, modifiedTooltip, attendanceStatus, nationalTeam, t } = ctx;
  const isBye = !result || !result.participant;

  const base = {
    slotProps: {
      'data-testid': 'bracket-slot',
      'data-round': result ? result.round : undefined,
      'data-pair': result ? result.pair : undefined,
      'data-pair-type': result ? result.pairType : undefined,
      'data-win': result && result.win !== null && result.win !== undefined ? String(result.win) : 'pending',
      'data-participant': result && result.participant ? result.participant.id : '',
      'data-draggable': 'false',
    },
  };

  if (isBye) {
    return { ...base, empty: true };
  }

  const info = infoOf(result.participant) || {};
  const name = info.name || '—';
  const isWinner = result.win === true;
  const isLoser = result.win === false;

  const canClick = clickable && onSlotClick;
  const canDrag = draggable && onSlotSwap;

  const slotProps = {
    ...base.slotProps,
    'data-draggable': canDrag ? 'true' : 'false',
    // Published so a test can assert the slot is UNCLICKABLE, not merely that
    // clicking it happened to do nothing: a fight already decided on a protocol
    // must not invite the click in the first place.
    'data-clickable': canClick ? 'true' : 'false',
    // The hover card (CompetitorCard `details`) replaces the plain tooltip
    // where there is one — two tooltips on one name would cover each other.
    title: info.details ? undefined : (canClick ? `${name} — ${t('bracket.clickToWin', 'click to mark as winner')}` : name),
    className: [canClick ? classes.slotClickable : '', canDrag ? classes.slotDraggable : ''].filter(Boolean).join(' ') || undefined,
  };

  if (canClick) {
    slotProps.onClick = () => onSlotClick(result);
  }
  if (canDrag) {
    slotProps.draggable = true;
    slotProps.onDragStart = e => {
      e.dataTransfer.setData('text/plain', result.id);
      e.dataTransfer.effectAllowed = 'move';
    };
    slotProps.onDragOver = e => e.preventDefault();
    slotProps.onDrop = e => {
      e.preventDefault();
      const sourceId = e.dataTransfer.getData('text/plain');
      if (sourceId && sourceId !== result.id) onSlotSwap(sourceId, result);
    };
  }

  return {
    name,
    // The athlete's competition number, rendered by CompetitorCard before
    // the name. Null when the competition does not use numbers.
    number: info.number || null,
    // Club prints on the ENTRY round only — the same rule the
    // PDF protocol follows in sub/team_name_column.html.twig. A later round
    // is reached, not entered: the athlete has already been identified by
    // club once, and repeating it costs the width the full name needs.
    club: isEntryRound ? info.club || null : null,
    // The COUNTRY in every round (22 Oct 2026: «країни будемо показувати у
    // всіх раундах») — a short code, unlike the club, so it costs no width.
    country: info.country || null,
    countryColor: info.countryColor || null,
    details: info.details || null,
    detailsHint: canClick ? t('bracket.clickToWin', 'click to mark as winner') : null,
    rank: info.rank || null,
    qualification: info.qualification || null,
    winner: isWinner,
    // Attendance Check-In: a non-PRESENT athlete's badge is more specific
    // and more useful than a plain "loser" (their result already reflects
    // it — the auto-advance sets win=false — the badge just explains why).
    // Never overrides 'winner': a non-PRESENT athlete cannot win.
    status: attendanceStatus || (isLoser ? 'loser' : null),
    nationalTeam: !!nationalTeam,
    annotation: annotation || null,
    modified: modifiedTooltip || null,
    slotProps,
  };
}

/**
 * Split a built tree into the two halves of the MIRRORED sheet.
 *
 * The official MSAC form draws the first half of the draw left-to-right on
 * the left, the second half right-to-left on the right, and the final between
 * them. Every round splits the same way — its top matches belong to the left
 * half and its bottom matches to the right — so one pass over the already
 * positioned rounds produces both sides.
 *
 * The right half's matches keep the y positions they were given for the whole
 * tree, so they are shifted up to start at the top of their own column.
 * Nothing about the tree maths changes; this only decides what goes where.
 */
function splitMirrored(rounds) {
  if (rounds.length < 2) return null;

  const treeRounds = rounds.slice(0, -1);
  const finalRound = rounds[rounds.length - 1];

  const half = (pick) => treeRounds.map(round => {
    const mid = Math.ceil(round.matches.length / 2);
    return { ...round, matches: pick(round.matches, mid) };
  });

  const left = half((matches, mid) => matches.slice(0, mid));
  const right = half((matches, mid) => matches.slice(mid));

  // Nothing landed on the right (an odd, tiny draw) — not a mirrored sheet.
  if (right.every(round => round.matches.length === 0)) return null;

  const tops = right.flatMap(round => round.matches.map(m => m.topY));
  const offset = tops.length ? Math.min(...tops) : 0;
  const shifted = right.map(round => ({
    ...round,
    matches: round.matches.map(m => ({ ...m, topY: m.topY - offset, centerY: m.centerY - offset })),
  }));

  return { left, right: shifted, finalRound };
}

// ─── BracketMatch (MatchCard wrapper) ──────────────────────────────────────────

function BracketMatch({ match, ctxFor, firstRound }) {
  const ctx = ctxFor();
  const classes = ctx.classes;
  const decided =
    (match.player1 && match.player1.win === true) ||
    (match.player2 && match.player2.win === true);

  const aka = makeSlotDescriptor(match.player1, ctxFor(match.player1), firstRound);
  const shiro = makeSlotDescriptor(match.player2, ctxFor(match.player2), firstRound);

  // The fight's own sheet, when one exists. Either slot answers: a protocol
  // belongs to the FIGHT, not to an athlete.
  const protocol = ctx.protocolFor
    ? (ctx.protocolFor(match.player1) || ctx.protocolFor(match.player2))
    : null;
  const row = match.player1 || match.player2;
  const live = Boolean(protocol && 'RUNNING' === protocol.state);
  // NEXT ON THE MAT — what the wall announces, the same reading
  // (TatamiWorkspaceReadModel::matFightsOf), orange where LIVE is blue.
  const isFight = ref => Boolean(ref && row && ref.round === row.round && Number(ref.pair) === Number(row.pair));
  const next = !live && isFight(ctx.nextFight);
  // THE MAT'S FIGHT, NOT STARTED YET (26 Oct 2026: «для поєдинку, який зараз
  // відбудеться, поки не розпочався… третій статус»): no sheet, or one opened
  // and not started. Started, it is LIVE; finished, it awaits confirmation.
  const ready = !live && isFight(ctx.currentFight) && (!protocol || 'PENDING' === protocol.state);

  return (
    <>
      <MatchCard
        aka={aka}
        shiro={shiro}
        decided={decided}
        firstRound={firstRound}
        dividerSpacing={INNER_SPACING}
        size='sm'
        className={classes.matchAnchor}
        style={{ top: match.topY }}
        containerProps={{
          'data-testid': 'bracket-match',
          'data-match-id': match.id,
          'data-state': decided ? 'decided' : 'pending',
          'data-protocol': protocol ? 'yes' : 'no',
        }}
      />
      {/* LIVE — the bout being fought right now (26 Oct 2026: «показуємо
          бадж LIVE там, де відбувається зараз поєдинок»): its sheet started
          and not yet finished; a pause is still live. */}
      {ready && (
        <span
          className={`${classes.liveBadge} ${classes.readyBadge}`}
          style={{ top: match.centerY - 8 }}
          data-testid='bracket-ready'
          data-round={row.round}
          data-pair={row.pair}
        >
          {ctx.t('workspace.matches.ready', 'READY')}
        </span>
      )}
      {next && (
        <span
          className={`${classes.liveBadge} ${classes.nextBadge}`}
          style={{ top: match.centerY - 8 }}
          data-testid='bracket-next'
          data-round={row.round}
          data-pair={row.pair}
        >
          {ctx.t('workspace.matches.next', 'NEXT')}
        </span>
      )}
      {live && (
        <span
          className={classes.liveBadge}
          style={{ top: match.centerY - 8 }}
          data-testid='bracket-live'
          data-round={protocol.round}
          data-pair={protocol.pair}
        >
          {ctx.t('workspace.matches.live', 'LIVE')}
        </span>
      )}
      {protocol && ctx.onProtocolOpen && (
        <button
          type='button'
          className={classes.protocolButton}
          style={{ top: match.centerY - 9 }}
          data-testid='bracket-protocol-button'
          data-round={protocol.round}
          data-pair={protocol.pair}
          title={ctx.t('workspace.matches.openProtocol', 'Show the fight protocol')}
          onClick={() => ctx.onProtocolOpen(protocol)}
        >
          i
        </button>
      )}
    </>
  );
}

// ─── Connectors ───────────────────────────────────────────────────────────────
//
//  All lines connect to match.centerY — never to individual player positions.

function Connectors({ prevMatches, nextMatches, colHeight, classes }) {
  const stroke = '#bdbdbd';
  const sw     = 1.5;
  const lines  = [];

  nextMatches.forEach((next, i) => {
    const f1 = prevMatches[i * 2];
    const f2 = prevMatches[i * 2 + 1];
    if (!f1) return;

    lines.push(
      <line key={`a-${next.id}`} x1={0} y1={f1.centerY} x2={STUB} y2={f1.centerY} stroke={stroke} strokeWidth={sw} />
    );

    if (f2) {
      lines.push(
        <line key={`b-${next.id}`} x1={0} y1={f2.centerY} x2={STUB} y2={f2.centerY} stroke={stroke} strokeWidth={sw} />,
        <line key={`c-${next.id}`} x1={STUB} y1={f1.centerY} x2={STUB} y2={f2.centerY} stroke={stroke} strokeWidth={sw} />
      );
    }

    lines.push(
      <line key={`d-${next.id}`} x1={STUB} y1={next.centerY} x2={CONN_W} y2={next.centerY} stroke={stroke} strokeWidth={sw} />
    );
  });

  return (
    <svg className={classes.connSvg} width={CONN_W} height={colHeight}>
      {lines}
    </svg>
  );
}

// ─── BracketView ─────────────────────────────────────────────────────────────

/**
 * Visual tournament bracket — PURE RENDERING built ENTIRELY on the
 * Competition Design System (MatchCard → CompetitorCard). No business logic,
 * no API calls, no interaction decisions — WHO may click / drag and WHAT it
 * means is the caller's concern (see InteractiveBracketView / DrawModule).
 *
 * Props:
 *  results             Flat array of competition result rows from the API.
 *  getCompetitorInfo   (participantRef) => { name, club, rank, qualification }
 *                      — the rich competitor data the design-system card shows.
 *  getParticipantName  (participantRef) => string — backward-compatible
 *                      name-only fallback when getCompetitorInfo is absent.
 *  clickableSlots      Paint occupied slots clickable and emit onSlotClick.
 *  onSlotClick         (result) => void — the clicked slot's row, verbatim.
 *  clickableRounds     Restrict winner clicks to these rounds (null = all).
 *  draggableFirstRound First-round drag & drop swap (draft editing).
 *  onSlotSwap          (sourceResultId, targetResult) => void.
 *  modifiedTooltips    { [participantId]: tooltip } — "manually moved" marker.
 *  slotAnnotations     { [resultId]: node } — per-slot annotation (e.g. score).
 *  getAttendanceStatus (participantRef) => badgeKey|null — attendance axis.
 *  getNationalTeamMember (participantRef) => bool — a SEPARATE, independent
 *                      classification axis (never a status value).
 */
export default function BracketView(props) {
  const {
    results, getCompetitorInfo, getParticipantName,
    clickableSlots, onSlotClick, clickableRounds,
    draggableFirstRound, onSlotSwap, modifiedTooltips, slotAnnotations,
    getAttendanceStatus, getNationalTeamMember, roundOrder, layout, roundStages,
    // THE FIGHT'S OWN SHEET. `protocolFor(result)` answers with the protocol of
    // the fight that row belongs to, or null. Two things follow from it: the
    // button that opens the sheet, and the slot being UNCLICKABLE — a fight
    // decided through a protocol must not be re-decided by a stray click here.
    protocolFor, onProtocolOpen, nextFight, currentFight,
  } = props;
  const classes = useStyles();
  const [t] = useTranslation();
  const roundLabel = useRoundLabel();
  const { rounds, thirdPlace } = buildBracketData(results, roundOrder);

  if (!rounds.length) return null;

  // FUKUGO alternates kata and kumite round by round. The discipline is the
  // BACKEND's answer (execution.rounds[].fukugoStage) — this renders it and
  // derives nothing, so the screen cannot disagree with the printed protocol.
  // Null for every other category, which is every other caller.
  const stageOf = code => (roundStages ? roundStages[code] : null) || null;
  const renderStage = code => {
    const stage = stageOf(code);
    if (!stage) return null;

    return (
      <div className={classes.roundStage} data-testid={`round-stage-${code}`} data-stage={stage}>
        {t(`operations.fukugo.stage.${stage}`)}
      </div>
    );
  };

  const canConduct = Boolean(clickableSlots && onSlotClick);
  const roundClickable = round => canConduct && (!clickableRounds || clickableRounds.includes(round));
  const canDrag = Boolean(draggableFirstRound && onSlotSwap);

  // Backward-compatible resolution: prefer the rich competitor info, fall
  // back to a name-only object for callers that only pass getParticipantName.
  const infoOf = participantRef => {
    if (getCompetitorInfo) return getCompetitorInfo(participantRef) || {};
    return { name: getParticipantName ? getParticipantName(participantRef) : null };
  };

  // Per-match context factory: capture the interaction rules of this round,
  // then per-result the annotation/modified marker keyed by the right id.
  const protocolOf = result => (protocolFor && result ? protocolFor(result) : null);

  /*
   * WHETHER THE FIGHT THIS ROW BELONGS TO STILL HAS A WINNER.
   *
   * Read off the rows themselves — `win === true` on either side is what the
   * engine calls a decided fight (FlagStageExecutor::isComplete). Used only to
   * tell a signed sheet that still matches its bracket from one left behind by
   * a re-draw; see isDecidedProtocol.
   */
  const decidedFights = (results || []).reduce((acc, row) => {
    const key = `${row.round}|${row.pair}`;
    acc[key] = acc[key] || true === row.win;

    return acc;
  }, {});
  const fightDecidedOf = result => (
    result ? Boolean(decidedFights[`${result.round}|${result.pair}`]) : true
  );

  /*
   * WHERE EACH ATHLETE LEFT THE BRACKET — the last round they stand in. An
   * attendance mark (absent, withdrawn, disqualified) belongs THERE only
   * (organiser, 9 Oct 2026: «якщо був дискваліфікований в певному раунді, то
   * в тому раунді і показуємо»): the rounds fought before are as they were.
   * Measured over EVERY row of the category, not only the rounds drawn
   * here: an athlete who went on to a scored final left there, and the mark
   * is in that table, as on the printed protocol.
   */
  const lastStageOf = (results || []).reduce((acc, row) => {
    const id = row.participant && row.participant.id;
    const stage = stageIndex(row.round);
    if (id && (undefined === acc[id] || stage > acc[id])) {
      acc[id] = stage;
    }

    return acc;
  }, {});

  const ctxFactory = (roundCode, allowDrag) => result => ({
    classes,
    infoOf,
    t,
    protocolFor,
    onProtocolOpen,
    nextFight,
    currentFight,
    // A fight DECIDED THROUGH A PROTOCOL is not re-decided by clicking a name
    // here. The sheet is the record — it has a timeline, a score and a
    // signature — and one stray click on this screen would overwrite it with
    // a bare winner. Asked per slot rather than per round, because a category
    // is half conducted at the mat and half from this screen all the time.
    /*
     * A SIGNED SHEET PROTECTS ITS FIGHT. An open one does not.
     *
     * This used to read `null === protocolOf(result)` — any protocol at all
     * made the slot unclickable. That is right for a CONFIRMED one, which
     * carries a timeline, scores and a signature that a stray click would
     * overwrite with a bare winner. It is wrong for every other state: a
     * sheet that was opened and abandoned has decided nothing, and it left
     * the fight impossible to finish from anywhere — the mat's computer was
     * gone and the bracket refused (1 Oct 2026).
     */
    clickable: roundClickable(roundCode)
      && !isDecidedProtocol(protocolOf(result), fightDecidedOf(result)),
    draggable: allowDrag,
    onSlotClick,
    onSlotSwap,
    annotation: result && slotAnnotations ? (slotAnnotations[result.id] || null) : null,
    modifiedTooltip: result && result.participant && modifiedTooltips
      ? (modifiedTooltips[result.participant.id] || null)
      : null,
    attendanceStatus: result && result.participant && getAttendanceStatus
      && lastStageOf[result.participant.id] === stageIndex(roundCode)
      ? getAttendanceStatus(result.participant)
      : null,
    nationalTeam: result && result.participant && getNationalTeamMember
      ? getNationalTeamMember(result.participant)
      : false,
  });

  const r1last = rounds[0].matches[rounds[0].matches.length - 1];
  let colH = r1last ? r1last.topY + MATCH_HEIGHT + 4 : 0;
  if (thirdPlace) {
    colH = Math.max(colH, thirdPlace.topY + MATCH_HEIGHT + 4 + 24);
  }
  const labelH = 24;

  /**
   * One side's worth of columns. Extracted so the mirrored layout draws its
   * right half with the SAME renderer as the left — the halves cannot drift
   * apart, because there is only one of them.
   */
  const renderColumns = (cols, { flipped = false, showThirdPlace = true, height = colH } = {}) => (
    <div className={[classes.columns, flipped ? classes.flipped : ''].filter(Boolean).join(' ')}>
      {cols.map((round, rIdx) => {
        const isLast = rIdx === cols.length - 1;
        const allowDrag = canDrag && rIdx === 0;

        return (
          <React.Fragment key={round.code}>
            <div className={classes.colWrapper}>
              {/* Flipped back: only the connector lines are meant to mirror,
                  never a name or a clickable slot. */}
              <div className={[classes.roundLabel, flipped ? classes.unflip : ''].filter(Boolean).join(' ')}>
                {roundLabel(round.code)}
                {renderStage(round.code)}
              </div>
              <div className={classes.roundCol} style={{ height }}>
                {round.matches.map(m => (
                  <div key={m.id} className={flipped ? classes.unflip : undefined}>
                    <BracketMatch
                      match={m}
                      firstRound={rIdx === 0}
                      ctxFor={ctxFactory(round.code, allowDrag)}
                    />
                  </div>
                ))}

                {isLast && showThirdPlace && thirdPlace && (
                  <React.Fragment>
                    <div className={classes.matchLabel} style={{ top: thirdPlace.topY - 16 }}>
                      {roundLabel(THIRD_PLACE)}
                      {renderStage(THIRD_PLACE)}
                    </div>
                    <BracketMatch
                      match={thirdPlace}
                      firstRound={false}
                      ctxFor={ctxFactory(THIRD_PLACE, false)}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>

            {!isLast && (
              <div className={classes.connSpace} style={{ height: height + labelH }}>
                <div style={{ height: labelH }} />
                <Connectors
                  prevMatches={round.matches}
                  nextMatches={cols[rIdx + 1].matches}
                  colHeight={height}
                  classes={classes}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // MIRRORED (official MSAC sheet): halves facing a central final.
  const halves = layout === 'mirrored' ? splitMirrored(rounds) : null;
  if (halves) {
    const finalMatch = halves.finalRound.matches[0];
    // EACH HALF HOLDS HALF THE ENTRY ROUND (18 Oct 2026: «не зрозуміло, чому
    // тут пусте місце, і так багато»): the height is the taller half's, not
    // the whole linear tree's — that left an empty strip as tall as the tree
    // under both halves.
    const halfHeight = Math.max(...[halves.left, halves.right].map(cols => {
      const entry = cols[0] ? cols[0].matches : [];
      const last = entry[entry.length - 1];

      return last ? last.topY + MATCH_HEIGHT + 4 : 0;
    }));

    return (
      <div
        className={classes.root}
        data-testid='bracket-view'
        data-layout='mirrored'
        data-interactive={canConduct ? 'true' : 'false'}
      >
        <div className={classes.mirrored}>
          {renderColumns(halves.left, { showThirdPlace: false, height: halfHeight })}

          <div className={classes.centreFinal} data-testid='bracket-final'>
            <div className={classes.roundLabel}>{roundLabel(halves.finalRound.code)}</div>
            {finalMatch && (
              /* A COLUMN'S WIDTH, not nothing: the card inside is placed
                 absolutely, left:0 right:0, so a box with no width of its
                 own collapsed the whole final to one vertical line — the
                 centre of every MSAC sheet showed «ФІНАЛ» over nothing, and
                 the final could not be clicked (4 Oct 2026). */
              <div style={{ position: 'relative', height: MATCH_HEIGHT, width: COL_W }} data-testid='bracket-final-match'>
                <BracketMatch
                  // centerY with it — the badge and the «i» are placed by
                  // it, and kept the column layout's value: the final's
                  // «NEXT» hung far below its card (26 Oct 2026).
                  match={{ ...finalMatch, topY: 0, centerY: MATCH_HEIGHT / 2 }}
                  firstRound={false}
                  ctxFor={ctxFactory(halves.finalRound.code, false)}
                />
              </div>
            )}
          </div>

          {renderColumns(halves.right, { flipped: true, showThirdPlace: false, height: halfHeight })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classes.root}
      data-testid='bracket-view'
      data-layout='linear'
      data-interactive={canConduct ? 'true' : 'false'}
      data-draggable={canDrag ? 'true' : 'false'}
    >
      <div className={classes.columns}>
        {rounds.map((round, rIdx) => {
          const isLast = rIdx === rounds.length - 1;
          const allowDrag = canDrag && rIdx === 0;

          return (
            <React.Fragment key={round.code}>

              {/* ── Round column ── */}
              <div className={classes.colWrapper}>
                <div className={classes.roundLabel}>
                  {roundLabel(round.code)}
                  {renderStage(round.code)}
                </div>
                <div className={classes.roundCol} style={{ height: colH }}>
                  {round.matches.map(m => (
                    <BracketMatch
                      key={m.id}
                      match={m}
                      firstRound={rIdx === 0}
                      ctxFor={ctxFactory(round.code, allowDrag)}
                    />
                  ))}

                  {/* Third-place fight: detached, below the final */}
                  {isLast && thirdPlace && (
                    <React.Fragment>
                      <div className={classes.matchLabel} style={{ top: thirdPlace.topY - 16 }}>
                        {roundLabel(THIRD_PLACE)}
                      {renderStage(THIRD_PLACE)}
                      </div>
                      <BracketMatch
                        match={thirdPlace}
                        firstRound={false}
                        ctxFor={ctxFactory(THIRD_PLACE, false)}
                      />
                    </React.Fragment>
                  )}
                </div>
              </div>

              {/* ── Connector channel (omitted after the last round) ── */}
              {!isLast && (
                <div className={classes.connSpace} style={{ height: colH + labelH }}>
                  <div style={{ height: labelH }} />
                  <Connectors
                    prevMatches={round.matches}
                    nextMatches={rounds[rIdx + 1].matches}
                    colHeight={colH}
                    classes={classes}
                  />
                </div>
              )}

            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

BracketView.propTypes = {
  results:            PropTypes.array.isRequired,
  /** (result) => protocol|null — the sheet the fight this row belongs to ran on. */
  protocolFor: PropTypes.func,
  /** (protocol) => void — open it. Without this the button is not rendered. */
  onProtocolOpen: PropTypes.func,
  // The fight the mat runs next — { round, pair } (26 Oct 2026).
  nextFight: PropTypes.shape({ round: PropTypes.string, pair: PropTypes.number }),
  // The mat's current fight — { round, pair }.
  currentFight: PropTypes.shape({ round: PropTypes.string, pair: PropTypes.number }),
  /** 'linear' (default) or 'mirrored' — the BACKEND decides, never this. */
  layout:             PropTypes.string,
  getCompetitorInfo:  PropTypes.func,
  getParticipantName: PropTypes.func,
  getAttendanceStatus: PropTypes.func,
  getNationalTeamMember: PropTypes.func,
  clickableSlots:     PropTypes.bool,
  onSlotClick:        PropTypes.func,
  draggableFirstRound: PropTypes.bool,
  onSlotSwap:          PropTypes.func,
  modifiedTooltips:    PropTypes.object,
  clickableRounds:     PropTypes.array,
  slotAnnotations:     PropTypes.object,
  /**
   * FUKUGO only: { roundCode: 'kata' | 'kumite' }, straight from the backend's
   * execution payload. Omitted by every other caller.
   */
  roundStages:         PropTypes.object,
};

BracketView.defaultProps = {
  results: [],
  roundStages: null,
  getCompetitorInfo: null,
  getParticipantName: null,
  getAttendanceStatus: null,
  getNationalTeamMember: null,
  clickableSlots: false,
  onSlotClick: null,
  draggableFirstRound: false,
  onSlotSwap: null,
  modifiedTooltips: null,
  clickableRounds: null,
  slotAnnotations: null,
};
